import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import type { BnDiagnosticSeverity } from "./bn-field.js";

let uid = 0;

/**
 * bn-input is a token-styled text control. It emits a composed `input`
 * CustomEvent and mirrors the value on the `value` property, so pages bind it
 * like a native input: `.value=${x} @input=${(e) => (x = e.detail.value)}`.
 *
 * Beyond the invalid *state*, it carries the invalid *message*: the policy
 * linter (`SC-F529`) anchors its findings to individual form fields, so a
 * finding is set as `diagnostic` (the message), `rule` (the rule id) and
 * `severity`. The message renders inside this component's own shadow root,
 * which is what lets it be associated with the control by `aria-describedby` —
 * a wrapper in another root cannot do that. Field-level errors that need no
 * association, or that carry rich content, belong on the surrounding bn-field.
 *
 * Properties:
 * - `value`        — the text value; mirrored on every keystroke
 * - `type`         — native input type (default `text`)
 * - `name`         — form field name; defaults to a generated stable id
 * - `placeholder`, `autocomplete`, `inputmode` — passed through
 * - `label`        — accessible name, set via the `aria-label` attribute;
 *                    bn-field fills this in from its own label
 * - `disabled`     — blocks interaction
 * - `invalid`      — error styling and `aria-invalid`; reflected, so a page can
 *                    select `bn-input[invalid]`
 * - `mono`         — monospace value (names, digests, secret references)
 * - `diagnostic`   — linter message shown under the control
 * - `rule`         — linter rule id, e.g. `L01`, shown as a chip on the message
 * - `severity`     — `error` (default) | `blocking-warning` | `warning`;
 *                    colours the border and the message
 *
 * Events:
 * - `input` — `CustomEvent<{ value: string }>`, bubbles and composed. The native
 *   `input` event is stopped at the shadow boundary so a page only ever sees
 *   this one.
 *
 *   <bn-input .value=${name} @input=${(e) => (this.name = e.detail.value)}></bn-input>
 *   <bn-input mono .value=${table} rule="L01" severity="error"
 *             diagnostic="A dotted name never matches — use one segment."></bn-input>
 */
@customElement("bn-input")
export class BnInput extends LitElement {
  @property() value = "";
  @property() type = "text";
  @property() name = "";
  @property() placeholder = "";
  @property() autocomplete = "";
  @property() inputmode = "";

  /**
   * Accessible name for the internal control. Bound to the `aria-label`
   * attribute so both `<bn-input aria-label="…">` and bn-field's automatic
   * label forwarding reach the real <input> inside this shadow root.
   */
  @property({ attribute: "aria-label" }) label = "";

  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean, reflect: true }) invalid = false;
  @property({ type: Boolean }) mono = false;

  /** The linter message. Present implies invalid styling at its severity. */
  @property() diagnostic = "";
  /** Linter rule id (`SC-F529`), e.g. `L01`. */
  @property() rule = "";
  @property() severity: BnDiagnosticSeverity = "error";

  /** Stable ids so the control isn't an anonymous form field and the message can be referenced. */
  private readonly fieldId = `bn-input-${++uid}`;
  private readonly diagId = `${this.fieldId}-diag`;

  static override styles = css`
    :host {
      display: block;
    }
    input {
      width: 100%;
      box-sizing: border-box;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      padding: 0.5rem 0.6875rem;
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
      transition:
        border-color var(--bino-transition-fast),
        box-shadow var(--bino-transition-fast);
    }
    input.mono {
      font-family: var(--bino-font-mono);
    }
    input::placeholder {
      color: var(--bino-text-secondary);
    }
    input:focus {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    input.error {
      border-color: var(--bino-error);
    }
    input.error:focus {
      box-shadow: 0 0 0 3px var(--bino-error-bg);
    }
    input.warning {
      border-color: var(--bino-warning);
    }
    input.warning:focus {
      box-shadow: 0 0 0 3px var(--bino-warning-bg);
    }
    input:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .diag {
      display: flex;
      align-items: baseline;
      gap: var(--bino-space-xs);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      margin: 0.375rem 0 0;
    }
    .diag.error {
      color: var(--bino-error);
    }
    .diag.warning {
      color: var(--bino-warning);
    }
    .rule {
      flex: none;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      padding: 0 var(--bino-space-xs);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-inset);
    }
  `;

  /** error | warning | "" — what the border and the message are coloured by. */
  private get tone(): "error" | "warning" | "" {
    if (this.diagnostic) return this.severity === "error" ? "error" : "warning";
    return this.invalid ? "error" : "";
  }

  private onInput(e: Event) {
    // The native `input` event is composed, so it would otherwise escape the
    // shadow root and reach the page's own `@input` handler alongside the
    // CustomEvent below — but with `detail` = 0, so `e.detail.value` is
    // undefined and clobbers the page's bound state (rendering the literal
    // "undefined" and making the field impossible to type in). Stop the native
    // event at the boundary so consumers only ever see our `{value}` event.
    e.stopPropagation();
    this.value = (e.target as HTMLInputElement).value;
    this.dispatchEvent(
      new CustomEvent("input", { detail: { value: this.value }, bubbles: true, composed: true }),
    );
  }

  override render() {
    const tone = this.tone;
    return html`
      <input
        class="${tone} ${this.mono ? "mono" : ""}"
        id=${this.fieldId}
        name=${this.name || this.fieldId}
        type=${this.type}
        .value=${this.value}
        placeholder=${this.placeholder}
        autocomplete=${this.autocomplete || "off"}
        inputmode=${this.inputmode || "text"}
        aria-label=${this.label || nothing}
        aria-invalid=${tone === "error" ? "true" : nothing}
        aria-describedby=${this.diagnostic ? this.diagId : nothing}
        ?disabled=${this.disabled}
        @input=${this.onInput}
      />
      ${this.diagnostic
        ? html`<p class="diag ${tone}" id=${this.diagId}>
            ${this.rule
              ? html`<code class="rule" title="Policy linter rule ${this.rule}">${this.rule}</code>`
              : ""}<span>${this.diagnostic}</span>
          </p>`
        : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-input": BnInput;
  }
}
